import { useState, useCallback, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import {
  Activity,
  AlertCircle,
  Calendar,
  CheckCircle,
  Clipboard,
  Clock,
  FileText,
  Heart,
  Info,
  MapPin,
  Navigation,
  Settings,
  Star,
  Target,
  TrendingUp,
  User,
  Users,
} from 'react-feather';
import { Pencil } from '../../icons/Pencil';

export const PATIENT_JOURNEY_ICONS = {
  Heart: { component: Heart, label: 'Heart' },
  Activity: { component: Activity, label: 'Activity' },
  User: { component: User, label: 'User' },
  Users: { component: Users, label: 'Users' },
  MapPin: { component: MapPin, label: 'Location' },
  Calendar: { component: Calendar, label: 'Calendar' },
  Clock: { component: Clock, label: 'Time' },
  Clipboard: { component: Clipboard, label: 'Clipboard' },
  FileText: { component: FileText, label: 'Document' },
  TrendingUp: { component: TrendingUp, label: 'Trending Up' },
  Target: { component: Target, label: 'Target' },
  Navigation: { component: Navigation, label: 'Navigation' },
  Settings: { component: Settings, label: 'Settings' },
  Star: { component: Star, label: 'Star' },
  CheckCircle: { component: CheckCircle, label: 'Check Circle' },
  AlertCircle: { component: AlertCircle, label: 'Alert Circle' },
  Info: { component: Info, label: 'Info' },
} as const;

export type IconName = keyof typeof PATIENT_JOURNEY_ICONS;

export type TagDetailsPatientJourneyIconProps = {
  label?: string;
  value?: string | null;
  /** If provided, renders a dropdown to choose an icon. */
  onChange?: (iconName: IconName) => void;
  themedColor?: string;
};

export const TagDetailsPatientJourneyIcon = ({
  label = 'Patient Journey Icon',
  value,
  onChange,
  themedColor,
}: TagDetailsPatientJourneyIconProps) => {
  const safeValue = (value as IconName) || 'Heart';
  const [current, setCurrent] = useState<IconName>(safeValue);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrent((value as IconName) || 'Heart');
  }, [value]);

  const selected = PATIENT_JOURNEY_ICONS[current] ?? PATIENT_JOURNEY_ICONS.Heart;
  const IconComponent = selected.component;

  const handleSelect = useCallback(
    (name: IconName) => {
      setCurrent(name);
      onChange?.(name);
      setIsOpen(false);
    },
    [onChange],
  );

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(prev => !prev);
  }, []);

  return (
    <>
      {isOpen && (
        <Backdrop
          onClick={e => {
            e.stopPropagation();
            setIsOpen(false);
          }}
        />
      )}
      <Root ref={containerRef}>
        <Label>{label}</Label>
        <Value>
          <IconSelector>
            <SelectedIcon>
              <IconComponent size={20} />
              <span>{selected.label}</span>
            </SelectedIcon>
            {onChange && (
              <IconEditButton
                onClick={handleToggle}
                type="button"
                title="Select icon"
                style={themedColor ? ({ '--themed-color': themedColor } as React.CSSProperties) : undefined}
              >
                <Pencil size={14} />
              </IconEditButton>
            )}
            {isOpen && (
              <IconDropdown onClick={e => e.stopPropagation()}>
                <IconGrid>
                  {Object.entries(PATIENT_JOURNEY_ICONS).map(([name, data]) => {
                    const IconComp = data.component;
                    return (
                      <IconOption key={name} onClick={() => handleSelect(name as IconName)}>
                        <IconComp size={18} />
                        <span>{data.label}</span>
                      </IconOption>
                    );
                  })}
                </IconGrid>
              </IconDropdown>
            )}
          </IconSelector>
        </Value>
      </Root>
    </>
  );
};

TagDetailsPatientJourneyIcon.displayName = 'TagDetails.PatientJourneyIcon';

import type React from 'react';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999;
  background: transparent;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-family: var(--font-bold);
  color: var(--gray-d);
  font-size: 14px;
`;

const Value = styled.div`
  min-height: 20px;
  display: flex;
  align-items: center;
`;

const IconSelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectedIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--black);
`;

const IconEditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid var(--themed-color, var(--pri-02));
  color: var(--themed-color, var(--pri-02));
  background: var(--white);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--themed-color, var(--pri-02));
    color: var(--white);
  }
`;

const IconDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1000;
  background: var(--white);
  border: 1px solid var(--gray-l);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 8px;
`;

const IconGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
`;

const IconOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 8px 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  color: var(--gray-d);

  &:hover {
    background: var(--gray-xxl);
    color: var(--black);
  }
`;
